import { query, where } from "firebase/firestore";

// Shared with every *QueryBuilder consumer that previously duplicated this list.
// One copy (Services/database.js:680, pre-migration) omitted "not-in" — keep a single
// source of truth here so that class of drift can't happen again.
export const INEQUALITY_OPS = ["<", "<=", ">", ">=", "!=", "not-in"];

// Operators whose "value" is a set. Splitting these into smaller chunks and
// summing per-chunk results is only correct when the chunks partition the
// document set with no overlap.
const DISJUNCTIVE_OPS = ["in", "not-in", "array-contains-any"];

// Only "in" (and "not-in") partition cleanly: a document matches at most one
// value of a scalar field, so disjoint value-chunks produce disjoint result
// sets and per-chunk counts/sums can be summed safely.
// "array-contains-any" has no such guarantee — a document's array can contain
// values from two different chunks, so it would be double-counted. Never chunk it.
const CHUNKABLE_OPS = ["in", "not-in"];

/**
 * Turn a flat triplet array ([field, op, value, field, op, value, ...]) — the
 * wire format every *QueryBuilder in Utils/Constants/ emits — into a list of
 * { field, operator, value } constraints.
 */
export function parseTriplets(filters) {
  if (!Array.isArray(filters) || filters.length === 0) return [];

  if (filters.length % 3 !== 0) {
    throw new Error("Invalid filter array: length must be a multiple of 3");
  }

  const constraints = [];
  for (let i = 0; i + 2 < filters.length; i += 3) {
    constraints.push({
      field: filters[i],
      operator: filters[i + 1],
      value: filters[i + 2]
    });
  }
  return constraints;
}

/**
 * Number of disjunctions a set of constraints expands to — the product of
 * the value-array length for every disjunctive constraint (in / not-in /
 * array-contains-any), 1 for everything else. This is what Firestore's
 * 30-disjunction ceiling is measured against.
 */
export function disjunctionCount(constraints) {
  return constraints.reduce((total, { operator, value }) => {
    if (DISJUNCTIVE_OPS.includes(operator) && Array.isArray(value)) {
      return total * Math.max(value.length, 1);
    }
    return total;
  }, 1);
}

function splitInHalf(values) {
  const mid = Math.ceil(values.length / 2);
  return [values.slice(0, mid), values.slice(mid)];
}

/**
 * Split a set of constraints into one or more plans, each under
 * maxDisjunctions. Only scalar "in"/"not-in" constraints are chunked (see
 * CHUNKABLE_OPS above) — the chunks are disjoint, so running one query per
 * plan and summing counts/aggregates across plans is exact.
 *
 * Throws if disjunctionCount can't be brought under the limit without
 * chunking a non-chunkable constraint (e.g. a lone "array-contains-any" with
 * more values than the limit allows) — callers should treat that as a filter
 * the user needs to narrow, not silently truncate it.
 */
export function planQueries(constraints, { maxDisjunctions = 30 } = {}) {
  let plans = [constraints];

  while (plans.some((plan) => disjunctionCount(plan) > maxDisjunctions)) {
    const nextPlans = [];
    let splitSomething = false;

    for (const plan of plans) {
      if (disjunctionCount(plan) <= maxDisjunctions) {
        nextPlans.push(plan);
        continue;
      }

      const chunkableIndices = plan
        .map((_, i) => i)
        .filter(
          (i) =>
            CHUNKABLE_OPS.includes(plan[i].operator) &&
            Array.isArray(plan[i].value) &&
            plan[i].value.length > 1
        );

      if (chunkableIndices.length === 0) {
        throw new Error(
          "Filter exceeds Firestore's disjunction limit and cannot be split further " +
            "(likely an array-contains-any or a single in-clause value set too large on its own)."
        );
      }

      // Split the largest chunkable set first — fewer total plans than splitting
      // whichever one happens to appear first.
      const chunkableIndex = chunkableIndices.reduce((largest, i) =>
        plan[i].value.length > plan[largest].value.length ? i : largest
      );

      const target = plan[chunkableIndex];
      const [firstHalf, secondHalf] = splitInHalf(target.value);

      [firstHalf, secondHalf].forEach((half) => {
        const splitPlan = plan.slice();
        splitPlan[chunkableIndex] = { ...target, value: half };
        nextPlans.push(splitPlan);
      });
      splitSomething = true;
    }

    plans = nextPlans;
    if (!splitSomething) break; // safety net against an infinite loop
  }

  return plans;
}

/**
 * Apply a plan's constraints to a Firestore CollectionReference/Query.
 */
export function applyConstraints(ref, constraints) {
  let q = query(ref);
  constraints.forEach(({ field, operator, value }) => {
    q = query(q, where(field, operator, value));
  });
  return q;
}
